/* eslint-disable */
import { useRef, useState, useEffect } from 'react';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Ticks } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BarChartSkeleton } from './Skeleton';
import {
	classes,
	formatNumber, inputHelper, useWindowDimensions
} from '../../../../Helpers';
import styles from './BarChart.module.scss';
import alert from '@/Images/alertImages.png';

const BarChart = (props) => {
	const {
		key = 'bar-chart',
		title,
		legendDisplay,
		maintainAspectRatio,
		data,
		className,
		loading,
		stackX,
		stackY,
		indexAxis,
		xAxisCallback,
		yAxisCallback,
		dataAttrs,
		showDataLabels,
		dataLabelFormatter,
		leftLabelName,
		rightLabelName,
		iconData,
		onIconClick,
		showIconPlugin = false,
		iconTooltipFormatter,
	} = props;

	const ref = useRef(null);
	const imgRef = useRef(null);
	const iconHitboxesRef = useRef([]);
	const containerRef = useRef(null);
	const iconDataRef = useRef(iconData);

	const [tooltip, setTooltip] = useState({
		visible: false,
		x: 0,
		y: 0,
		content: '',
	});

	useEffect(() => {
		iconDataRef.current = iconData;
	}, [iconData]);

	if (!imgRef.current) {
		const img = new Image();
		img.src = alert;
		img.onload = () => {
			if (ref.current && showIconPlugin) {
				ref.current.update('none');
			}
		};
		img.onerror = () => {
			console.error('❌ Failed to load alert image');
		};
		imgRef.current = img;
	}

	const onMouseMove = (event) => {
		const { current: chart } = ref;
		if (!chart || !showIconPlugin) {
			setTooltip({ visible: false, x: 0, y: 0, content: '' });
			return;
		}

		const canvas = chart.canvas;
		const rect = canvas.getBoundingClientRect();
		const x = event.nativeEvent.clientX - rect.left;
		const y = event.nativeEvent.clientY - rect.top;

		const hitboxes = iconHitboxesRef.current;
		for (const hitbox of hitboxes) {
			if (
				x >= hitbox.x &&
				x <= hitbox.x + hitbox.width &&
				y >= hitbox.y &&
				y <= hitbox.y + hitbox.height
			) {
				const tooltipContent = iconTooltipFormatter
					? iconTooltipFormatter(hitbox.data, hitbox.index)
					: `Label: ${hitbox.data?.label || 'N/A'}\nValue: ${hitbox.data?.value || 'N/A'}`;
				
				setTooltip({
					visible: true,
					x: event.nativeEvent.clientX,
					y: event.nativeEvent.clientY,
					content: tooltipContent,
				});
				return;
			}
		}
		setTooltip({ visible: false, x: 0, y: 0, content: '' });
	};

	const onMouseLeave = () => {
		setTooltip({ visible: false, x: 0, y: 0, content: '' });
	};

	const onClick = (event) => {
		const { current: chart } = ref;
		if (!chart) return;

		const canvas = chart.canvas;
		const rect = canvas.getBoundingClientRect();
		const x = event.nativeEvent.clientX - rect.left;
		const y = event.nativeEvent.clientY - rect.top;

		const hitboxes = iconHitboxesRef.current;
		for (const hitbox of hitboxes) {
			if (
				x >= hitbox.x &&
				x <= hitbox.x + hitbox.width &&
				y >= hitbox.y &&
				y <= hitbox.y + hitbox.height
			) {
				if (onIconClick) {
					onIconClick(hitbox.index, hitbox.data);
				}
				return;
			}
		}

		const elementAtEvent = getElementAtEvent(chart, event);
		const { dataset } = inputHelper(event);
		// eslint-disable-next-line react/destructuring-assignment
		props.onClick(elementAtEvent, dataset);
	};
	
	const { width } = useWindowDimensions();

	const iconPlugin = {
		id: 'iconPlugin',
		afterDatasetsDraw(chart) {
			const currentIconData = iconDataRef.current;
			if (!currentIconData || !currentIconData.length || !imgRef.current?.complete) {
				iconHitboxesRef.current = [];
				return;
			}
			
			const { ctx, chartArea, scales } = chart;
			const xScale = scales.x;
			
			if (!chartArea || !xScale) {
				iconHitboxesRef.current = [];
				return;
			}

			ctx.save();
			const hitboxes = [];

			currentIconData.forEach((showIcon, index) => {
				if (!showIcon) return;

				const x = xScale.getPixelForValue(index);
				
				let topY = Infinity;
				let topBar = null;
				const datasetCount = chart.data.datasets.length;
				
				for (let i = 0; i < datasetCount; i++) {
					const meta = chart.getDatasetMeta(i);
					const bar = meta.data[index];
					if (bar) {
						const barTop = Math.min(bar.y, bar.base);
						if (barTop < topY) {
							topY = barTop;
							topBar = bar;
						}
					}
				}

				if (topY !== Infinity) {
					const imgWidth = 20;
					const imgHeight = 20;
					const drawX = x - imgWidth / 2; 
					const drawY = topY - imgHeight - 10; 

					ctx.drawImage(imgRef.current, drawX, drawY, imgWidth, imgHeight);

					hitboxes.push({
						index,
						x: drawX,
						y: drawY,
						width: imgWidth,
						height: imgHeight,
						data: {
							label: chart.data.labels?.[index],
							value: chart.data.datasets?.[0]?.data?.[index],
							bar: topBar,
						}
					});
				}
			});

			iconHitboxesRef.current = hitboxes;
			ctx.restore();
		}
	};

	return (
		<div 
			className={styles.root} 
			ref={containerRef}
			onMouseMove={onMouseMove}
			onMouseLeave={onMouseLeave}
			style={{ position: 'relative' }}
		>
			<div className={classes(styles.labelName, styles.left)}>
				{leftLabelName}
			</div>
			<div className={classes(styles.labelName, styles.right)}>
				{rightLabelName}
			</div>
			<Bar
				key={key}
				ref={ref}
				onClick={onClick}
				data={data}
				options={{
					responsive: true,
					legend: {
						position: 'top',
						display: legendDisplay,
					},
					indexAxis,
					maintainAspectRatio,
					scales: {
						x: {
							stacked: stackX,
							ticks: {
								font: {
									size: 13,
									color: 'black',
								},
								callback(value, index, ticks) {
									return xAxisCallback(
										this,
										value,
										index,
										ticks
									);
								},
							},
						},
						y: {
							stacked: stackY,
							grid: {
								drawOnChartArea: false,
							},
							ticks: {
								font: {
									size: 12,
									color: 'black',
								},
								callback(value, index, ticks) {
									return yAxisCallback(
										this,
										value,
										index,
										ticks
									);
								},
							},
						},
						'right-y-axis': {
							type: 'linear',
							position: 'right',
							grid: {
								display: false,
							},
							ticks: {
								color: 'rgb(73, 113, 116)',
								font: {
									weight: 'bold',
								},
							},
						},
					},
					plugins: {
						datalabels: {
							align: 'start',
							padding: '0',
							anchor: 'center',
							maxRotation: 0,
							rotation: width > 600 ? 0 : 270,
							formatter: dataLabelFormatter,
							display: showDataLabels,
							clamp: false,
							color: '#000',
							font: {
								weight: '400',
								size: 9,
							},
						},
						title: {
							display: true,
							text: title,
							font: {
								color: 'black',
								size: 16,
							},
						},
					},
				}}
				className={className}
				{...dataAttrs}
				plugins={[ChartDataLabels, ...(showIconPlugin ? [iconPlugin] : [])]}
			/>
			{loading && <BarChartSkeleton />}
			
			{/* Tooltip */}
			{tooltip.visible && (
				<div
					style={{
						position: 'fixed',
						left: `${tooltip.x + 10}px`,
						top: `${tooltip.y - 10}px`,
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						color: 'white',
						padding: '8px 12px',
						borderRadius: '4px',
						fontSize: '12px',
						pointerEvents: 'none',
						zIndex: 10000,
						whiteSpace: 'pre-line',
						maxWidth: '250px',
						boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
					}}
				>
					{tooltip.content}
				</div>
			)}
		</div>
	);
};

BarChart.defaultProps = {
	title: '',
	legendDisplay: true,
	maintainAspectRatio: false,
	data: {
		datasets: [],
		labels: [],
	},
	showDataLabels: false,
	dataLabelFormatter: (context) => {
		if (context) {
			return formatNumber('IN', Math.round(context));
		}
		return '';
	},
	handleElementClick: () => {},
	className: '',
	maxBarThickness: 50,
	stackX: false,
	stackY: false,
	onClick: () => {},
	indexAxis: 'x',
	xAxisCallback(chartThis, value, index, ticks) {
		const newValue = chartThis.getLabelForValue(value);
		return Ticks.formatters.values.apply(chartThis, [
			newValue,
			index,
			ticks,
		]);
	},
	yAxisCallback(chartThis, value, index, ticks) {
		const newValue = chartThis.getLabelForValue(value);
		return Ticks.formatters.values.apply(chartThis, [
			newValue,
			index,
			ticks,
		]);
	},
	dataAttrs: {},
	leftLabelName: '',
	rightLabelName: '',
	iconData: null,
	onIconClick: null,
	showIconPlugin: false,
	iconTooltipFormatter: null, // Optional function: (data, index) => string
};

export default BarChart;
